import * as yup from 'yup';
import { Trans, useTranslation } from 'react-i18next';
import { TypedDocumentNode } from '@apollo/client';
import { useForm } from 'react-form-state-manager';

import { GetTicketQuery as GetTicket } from '__generated__/graphql';

import { getServerErrors } from '../../../../common/helpers';
import GetEventLayoutQuery from '../GetEventLayoutQuery';
import UI from '../../../../common/UI';
import useEditTicketMutation from './useEditTicketMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

type Event = GetTicket['event'];
type Ticket = GetTicket['ticket'];

const editTicketTeamsSchema = yup.object().shape({
  allow_individuals: yup.bool().when(['allow_create_team', 'allow_join_team'], {
    is: (allowCreateTeam: boolean, allowJoinTeam: boolean) => !allowCreateTeam && !allowJoinTeam,
    then: yup.bool().oneOf([true]),
  }),
  allow_create_team: yup.bool(),
  allow_join_team: yup.bool(),
});

export interface EditTicketTeamsFormProps {
  ticket: Ticket;
  event: Event
  refetchQueries?: TypedDocumentNode[];
}

const EditTicketTeamsForm = ({
  ticket, event, refetchQueries = [GetEventLayoutQuery],
}: EditTicketTeamsFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const getValues = (ticket: Ticket) => ({
    allow_individuals: ticket.allow_individuals,
    allow_create_team: ticket.allow_create_team,
    allow_join_team: ticket.allow_join_team,
  });

  const form = useForm({
    values: getValues(ticket),
  });

  useNavigationPrompt(form.changed());

  const [editTicket, { error, loading }] = useEditTicketMutation({
    input: {
      id: ticket.id,
      teams: form.values,
    },
    onSuccess: (ticket) => {
      form.reset(getValues(ticket));
    },
    refetchQueries,
  });

  const { valid, errors } = useValidation({
    schema: editTicketTeamsSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'teams'),
  });

  return (
    <UI.Form onSubmit={() => editTicket()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('teams')}
          </UI.Legend>
          <UI.InputDescription>
            {t('teams_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup
          sc={{
            valid: !errors.allow_individuals && !errors.allow_create_team && !errors.allow_join_team,
            touched: form.touched.allow_individuals || form.touched.allow_create_team
              || form.touched.allow_join_team,
          }}
        >
          <UI.FormGrid sc={{ gutter: 0.25 }}>
            <UI.Checkbox
              checked={form.values.allow_individuals}
              onChange={() => form.set('allow_individuals', (allow: boolean) => !allow)}
              sc={{ box: true }}
              style={{ flex: 1 }}
            >
              <UI.Strong sc={{ block: true }}>{t('allow_individuals')}</UI.Strong>
              {t('allow_individuals_description')}
            </UI.Checkbox>
            <UI.Checkbox
              checked={form.values.allow_create_team}
              onChange={() => form.set('allow_create_team', (allow: boolean) => !allow)}
              sc={{ box: true }}
              style={{ flex: 1 }}
            >
              <UI.Strong sc={{ block: true }}>{t('allow_create_team')}</UI.Strong>
              {t('allow_create_team_description')}
            </UI.Checkbox>
            <UI.Checkbox
              checked={form.values.allow_join_team}
              onChange={() => form.set('allow_join_team', (allow: boolean) => !allow)}
              sc={{ box: true }}
              style={{ flex: 1 }}
            >
              <UI.Strong sc={{ block: true }}>{t('allow_join_team')}</UI.Strong>
              {t('allow_join_team_description')}
            </UI.Checkbox>
          </UI.FormGrid>
        </UI.InputGroup>

        <UI.Div>
          <Trans
            i18nKey={event.private_teams
              ? 'event_private_teams_enabled_description'
              : 'event_private_teams_disabled_description'}
          >
            <UI.Link to={`/${project.id}/events/${event.id}/settings/restrictions`} />
          </Trans>
        </UI.Div>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_ticket')}
          closeTo={`/${project.id}/events/${event.id}/tickets`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditTicketTeamsForm;
